import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Database, Search, Plus, Trash2, Star, Filter, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["hook", "intro", "cta", "structure", "bio", "caption"];
const PLATFORMS = ["instagram", "tiktok", "youtube", "x", "linkedin"];
const CATEGORIES = ["curiosity", "controversy", "value", "transformation", "story", "fear", "desire", "general"];

export default function SwipeFile() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [fType, setFType] = useState("all");
  const [fPlat, setFPlat] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try { setItems(await api.listSwipe()); } catch { toast.error("Erreur"); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (fType !== "all" && it.type !== fType) return false;
      if (fPlat !== "all" && it.platform !== fPlat) return false;
      if (fCat !== "all" && it.category !== fCat) return false;
      if (search && !it.text.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, fType, fPlat, fCat, search]);

  const remove = async (id) => {
    if (!confirm("Supprimer ?")) return;
    await api.deleteSwipe(id);
    setItems(items.filter(i => i.id !== id));
  };

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="swipe-file-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// SWIPE FILE INTELLIGENT</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Bibliothèque chirurgicale</h1>
          <p className="text-zinc-400 mt-2 max-w-2xl">Hooks, intros, CTA, structures — filtrables au laser. Stocke ce qui marche, élimine le bruit.</p>
        </div>
        <button data-testid="add-swipe-btn" onClick={() => setShowAdd(true)}
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm glow-volt">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </header>

      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
          <input data-testid="swipe-search" placeholder="Rechercher dans tous les snippets..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm pl-10 pr-3 py-2 text-sm text-white placeholder-zinc-700 focus:border-[#CCFF00]/40 outline-none" />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-mono uppercase text-zinc-500 flex items-center gap-1"><Filter className="w-3 h-3" /> Filtres</span>
          <Filt label="Type" val={fType} setVal={setFType} opts={TYPES} testid="filter-type" />
          <Filt label="Plateforme" val={fPlat} setVal={setFPlat} opts={PLATFORMS} testid="filter-plat" />
          <Filt label="Catégorie" val={fCat} setVal={setFCat} opts={CATEGORIES} testid="filter-cat" />
          <div className="ml-auto text-[11px] font-mono text-zinc-500">{filtered.length} / {items.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.length === 0 ? (
          <div className="md:col-span-2 border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
            <Database className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
            <div className="text-zinc-500 text-sm">Aucun snippet — ajoute ton premier ou ajuste les filtres.</div>
          </div>
        ) : filtered.map((it, i) => (
          <div key={it.id} data-testid={`swipe-item-${i}`}
            className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 hover:border-white/15 transition group">
            <div className="flex items-start gap-3 mb-2">
              <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 bg-[#CCFF00]/10 text-[#CCFF00] border border-[#CCFF00]/30 rounded-sm">{it.type}</span>
              <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 bg-white/5 text-zinc-400 border border-white/10 rounded-sm">{it.platform}</span>
              <span className="text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 bg-white/5 text-zinc-400 border border-white/10 rounded-sm">{it.category}</span>
              <div className="ml-auto flex items-center gap-2">
                {it.score > 0 && <span className="font-mono text-xs text-[#CCFF00]">{it.score}/100</span>}
                <button onClick={() => remove(it.id)} className="opacity-0 group-hover:opacity-100 transition text-zinc-600 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="text-white text-sm leading-relaxed">{it.text}</div>
            {(it.source || it.notes) && (
              <div className="mt-3 pt-3 border-t border-[#1E1E24] flex items-center gap-3 text-[11px] text-zinc-500 font-mono">
                {it.source && <span>src: {it.source}</span>}
                {it.notes && <span className="truncate">// {it.notes}</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onCreated={(it) => { setItems([it, ...items]); setShowAdd(false); }} loading={loading} setLoading={setLoading} />}
    </div>
  );
}

const Filt = ({ label, val, setVal, opts, testid }) => (
  <select data-testid={testid} value={val} onChange={e => setVal(e.target.value)}
    className="bg-[#070708] border border-[#1E1E24] rounded-sm px-2.5 py-1 text-xs text-white focus:border-[#CCFF00]/40 outline-none">
    <option value="all">{label}: tous</option>
    {opts.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

function AddModal({ onClose, onCreated, loading, setLoading }) {
  const [f, setF] = useState({ type: "hook", platform: "instagram", category: "curiosity", text: "", source: "", notes: "", score: 0, niche: "" });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const it = await api.createSwipe({ ...f, score: Number(f.score) || 0 });
      toast.success("Ajouté à la swipe file");
      onCreated(it);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit}
        className="bg-[#0A0A0C] border border-[#1E1E24] rounded-sm p-6 w-full max-w-lg space-y-3" data-testid="add-swipe-modal">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xl text-white">Nouveau snippet</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Sel data-testid="new-type" val={f.type} setVal={v => setF({ ...f, type: v })} opts={TYPES} label="Type" />
          <Sel data-testid="new-plat" val={f.platform} setVal={v => setF({ ...f, platform: v })} opts={PLATFORMS} label="Plateforme" />
          <Sel data-testid="new-cat" val={f.category} setVal={v => setF({ ...f, category: v })} opts={CATEGORIES} label="Catégorie" />
        </div>

        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Texte</label>
          <textarea data-testid="new-text" required rows={3} value={f.text} onChange={e => setF({ ...f, text: e.target.value })}
            className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <input data-testid="new-source" placeholder="Source (@compte)" value={f.source} onChange={e => setF({ ...f, source: e.target.value })}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
          <input data-testid="new-score" type="number" min="0" max="100" placeholder="Score 0-100" value={f.score} onChange={e => setF({ ...f, score: e.target.value })}
            className="bg-[#070708] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none" />
        </div>

        <button type="submit" disabled={loading} data-testid="new-submit"
          className="w-full bg-[#CCFF00] text-black font-medium py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}{loading ? "Ajout..." : "Ajouter"}
        </button>
      </form>
    </div>
  );
}

const Sel = ({ val, setVal, opts, label, ...rest }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</label>
    <select value={val} onChange={e => setVal(e.target.value)} {...rest}
      className="w-full bg-[#070708] border border-[#1E1E24] rounded-sm px-2 py-2 text-sm text-white focus:border-[#CCFF00]/50 outline-none">
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
